import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Navigate, Outlet } from 'react-router';
import type { RouteObject } from 'react-router';

import { ErrorView } from '@jane/business-admin/components/ErrorView';
import { FeedbackButton } from '@jane/business-admin/components/feedback/FeedbackButton';
import { RumErrorBoundary as ErrorBoundary } from '@jane/business-admin/components/RumErrorBoundary';
import { ModalDecider } from '@jane/business-admin/components/stores/detail/products/modal/ModalDecider';
import { SpecialsModal } from '@jane/business-admin/components/stores/detail/specials/modal/SpecialsModal';
import { UnauthorizedAccess } from '@jane/business-admin/components/UnauthorizedAccess';
import { useManager } from '@jane/business-admin/data-access';
import { useHasPermissions } from '@jane/business-admin/hooks';
import { StoreAudit } from '@jane/business-admin/pages/StoreAudit';
import { StoreMenu } from '@jane/business-admin/pages/StoreMenu';
import { StoreProducts } from '@jane/business-admin/pages/StoreProducts';
import { StoreStaff } from '@jane/business-admin/pages/StoreStaff';
import { Permission } from '@jane/shared/auth';
import { load } from '@jane/shared/util';

const Stores = load(() => import('../features/Stores'));
const StoreDetail = load(() => import('../features/StoreDetail'));
const StoreSettings = load(() => import('../features/StoreSettings'));
const StoreSettingsDetails = load(
  () => import('../features/StoreSettingsDetails')
);
const StoreSettingsCheckout = load(
  () => import('../features/StoreSettingsCheckout')
);
const StoreSettingsSubdomain = load(
  () => import('../features/StoreSettingsSubdomain')
);
const StoreSettingsIntegrations = load(
  () => import('../features/StoreSettingsIntegrations')
);
const StoreSettingsFulfillments = load(
  () => import('../features/StoreSettingsFulfillments')
);
const StoreSettingsNotifications = load(
  () => import('../features/StoreSettingsNotifications')
);
const StoreSettingsHardware = load(
  () => import('../features/StoreSettingsHardware')
);

const StoreSettingsSpecials = load(
  () => import('../features/StoreSettingsSpecials')
);
const StoreSpecials = load(() => import('../features/StoreSpecials'));

const GatedAuditLogRoute = () => {
  const isAllowed = useFlag(FLAGS.scAuditLogsBeta);

  return !isAllowed ? (
    <UnauthorizedAccess resourceName="audit logs" variant="view" />
  ) : (
    <StoreAudit />
  );
};

const GatedMenuEditRoute = () => {
  const isAllowed = useHasPermissions([Permission.EditMenuCustomizations]);

  if (isAllowed === undefined) return null;

  return !isAllowed ? (
    <UnauthorizedAccess resourceName="menu customizations" />
  ) : (
    <StoreMenu />
  );
};

const GatedHardwareRoute = () => {
  const isAllowed = useHasPermissions([Permission.EditPrinters]);

  if (isAllowed === undefined) return null;

  return !isAllowed ? (
    <UnauthorizedAccess resourceName="printers" />
  ) : (
    <StoreSettingsHardware />
  );
};

// Client managers without edit_store are admitted; individual settings cards
// gate their own save buttons. Non-client roles must hold edit_store.
const GatedStoreSettingsRoute = () => {
  const isPermissionGateEnabled = useFlag(FLAGS.c1PermissionGate);
  const isAllowed = useHasPermissions([Permission.EditStore]);
  const { data: currentManager, isFetched } = useManager();

  if (!isPermissionGateEnabled) {
    if (!isFetched) return null;
    return currentManager?.manager?.role !== 'client' ? (
      <Navigate replace to="/" />
    ) : (
      <StoreSettings />
    );
  }

  if (isAllowed === undefined) return null;

  if (!isAllowed) {
    return currentManager?.manager?.role !== 'client' ? (
      <Navigate replace to="/" />
    ) : (
      <StoreSettings />
    );
  }

  return <StoreSettings />;
};

const FULFILLMENT_ONLY_PERMISSIONS = [
  Permission.EditFulfillments,
  Permission.AssignFulfillments,
];

// When the permission gate flag is off, restore the old role-based guard.
// Once the flag is fully rolled out and the role column is deprecated, replace
// this with per-route permission gates on the ungated leaf routes (products,
// specials, staff).
const GatedStoresRoute = () => {
  const isPermissionGateEnabled = useFlag(FLAGS.c1PermissionGate);
  const { data: currentManager, isFetched } = useManager();

  if (!isFetched) return null;

  if (!isPermissionGateEnabled && currentManager?.manager?.role !== 'client') {
    return <Navigate replace to="/" />;
  }

  if (isPermissionGateEnabled) {
    const role = currentManager?.manager?.role;
    const permissions = currentManager?.manager?.permissions || [];
    const hasBizdashAccess =
      role === 'client' ||
      permissions.some(
        (p) => !FULFILLMENT_ONLY_PERMISSIONS.includes(p as Permission)
      );

    if (!hasBizdashAccess) {
      return <Navigate replace to="/" />;
    }
  }

  return (
    <ErrorBoundary fallback={ErrorView} scope="stores">
      <Outlet />
      <FeedbackButton />
    </ErrorBoundary>
  );
};

export const storeRoutes = (): RouteObject[] => [
  {
    path: '/stores',
    element: <GatedStoresRoute />,
    children: [
      {
        index: true,
        element: <Stores />,
      },
      {
        path: ':storeId',
        element: <StoreDetail />,
        children: [
          {
            path: 'settings',
            element: <GatedStoreSettingsRoute />,
            children: [
              {
                path: 'details',
                element: <StoreSettingsDetails />,
              },
              {
                path: 'checkout',
                element: <StoreSettingsCheckout />,
              },
              {
                path: 'specials',
                element: <StoreSettingsSpecials />,
              },
              {
                path: 'subdomain',
                element: <StoreSettingsSubdomain />,
              },
              {
                path: 'integrations',
                element: <StoreSettingsIntegrations />,
              },
              {
                path: 'fulfillments',
                element: <StoreSettingsFulfillments />,
              },
              {
                path: 'notifications',
                element: <StoreSettingsNotifications />,
              },
              {
                path: 'hardware',
                element: <GatedHardwareRoute />,
              },
              {
                path: '/stores/:storeId/settings/',
                element: <Navigate to="details" replace={true} />,
              },
            ],
          },
          {
            path: '/stores/:storeId/',
            element: <Navigate to="settings/details" replace={true} />,
          },
          {
            path: 'menu',
            element: <GatedMenuEditRoute />,
          },
          {
            path: 'products/:type',
            element: <StoreProducts />,
            children: [
              {
                path: ':product_id',
                element: <ModalDecider />,
              },
            ],
          },
          {
            path: 'specials',
            element: <StoreSpecials />,
            children: [
              {
                path: ':special_id',
                element: <SpecialsModal />,
              },
              {
                path: 'duplicate/:special_id',
                element: <SpecialsModal />,
              },
            ],
          },
          {
            path: 'staff',
            element: <StoreStaff />,
          },
          {
            path: 'products',
            element: <Navigate to="published" />,
          },
          {
            path: 'specials',
            element: <Navigate to="live" />,
          },
          {
            path: 'audit',
            element: <GatedAuditLogRoute />,
          },
        ],
      },
    ],
  },
];
